import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("If that email exists, a reset link has been sent.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-6" data-testid="forgot-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 rounded-md bg-[#1C3F35] flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5}/>
          </div>
          <span className="font-heading text-lg">Beacon of Grants</span>
        </Link>

        <div className="label-eyebrow mb-3">Reset password</div>
        <h1 className="font-heading font-light text-4xl tracking-tighter text-[#1A1A1A]">
          Forgot your password?
        </h1>
        <p className="text-[#5E5E5E] mt-2">
          Enter your email and we'll send you a reset link.
        </p>

        {sent ? (
          <div className="mt-10 bg-white border border-[#E6E4DF] rounded-lg p-6" data-testid="forgot-sent">
            <p className="text-sm text-[#1A1A1A]">
              If <b>{email}</b> matches an account, you'll receive an email shortly with a reset link.
            </p>
            <p className="mt-3 text-xs text-[#5E5E5E]">
              The link expires in 1 hour. Check your spam folder if you don't see it.
            </p>
            <Link to="/login" className="mt-6 inline-block text-sm text-[#1C3F35] underline underline-offset-4">
              Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-5">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" type="email" required
                value={email} onChange={(e)=>setEmail(e.target.value)}
                data-testid="forgot-email"
                className="mt-2 bg-white border-[#E6E4DF]"
              />
            </div>
            <Button
              type="submit" disabled={loading}
              data-testid="forgot-submit"
              className="w-full bg-[#1C3F35] text-white hover:bg-[#1A362D] rounded-md py-6"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : "Send reset link"}
            </Button>
            <Link to="/login" className="block text-center text-sm text-[#5E5E5E] hover:text-[#1C3F35]">
              Back to sign in
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
